import { Button, Stack, Text } from '@mantine/core'
import { IconArrowBackUp } from '@tabler/icons-react'
import { PropsWithChildren } from 'react'
import { FormattedMessage } from 'react-intl'
import { Link } from 'react-router'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { RouterPath } from '@/router/paths'
import { useSession } from '@/session/SessionContext'
import { Resource, ResourceAction } from '@/types/permissions'

/**
 * Component to conditionally render children based on user permissions.
 *
 * Props:
 *   - permissions: A single [resource, action] pair defining the required permission.
 *   - children: Components to render if the permission is met.
 *
 * Example:
 *   <CanAccessRoute
 *     permissions={['ResourceA', 'Action1']}
 *   >
 *     <SomePage />
 *   </CanAccessRoute>
 */

type CanAccessRouteProps = PropsWithChildren<{
  permissions: [Resource, ResourceAction]
  devEnabled?: boolean
}>

export const CanAccessRoute = ({
  permissions,
  devEnabled,
  children
}: CanAccessRouteProps) => {
  const { permissions: userPermissions } = useSession()

  const [resource, action] = permissions
  const hasPermission =
    devEnabled || userPermissions[resource]?.includes(action)

  if (!hasPermission) {
    return (
      <PlatformAlert
        m="md"
        variant="warning"
        title={<FormattedMessage id="permissions.denied.title" />}
      >
        <Stack align="start">
          <Text size="sm">
            <FormattedMessage id="permissions.denied.description" />
          </Text>

          <Button
            size="xs"
            leftSection={<IconArrowBackUp size={16} />}
            component={Link}
            to={RouterPath.root}
          >
            <FormattedMessage id="returnHome" />
          </Button>
        </Stack>
      </PlatformAlert>
    )
  }

  return <>{children}</>
}
